import React, { FunctionComponent } from 'react';

import { formatNumber, Text } from '@tradingblock/components';
import { AchRelationship, Transfer } from '@tradingblock/types';
import styled from 'styled-components';
import { FormValues as TransferFormData } from '../views/FundingRequestStep';

const StyledFundingRequestReview = styled.div`
  .funding-detail, .funding-method-detail {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    margin-top: 1rem;
  }

  .funding-details, .funding-method-details {
    width: 100%;
    margin-top: 1rem;
  }

  .funding-detail:first-child, .funding-method-detail:first-child {
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    padding-top: 1rem;
  }

  .funding-detail:last-child, .funding-method-detail:last-child {
    margin-bottom: 1rem;
  }

  .funding-detail-label, .funding-method-detail-label {
    padding-right: 2rem;
  }

  .funding-detail-value-wrapper, .funding-method-detail-value-wrapper {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .funding-detail-value, .funding-method-detail-value {
    width: fit-content;
  }

  h5 {
    color: #ffffff;
    margin-bottom: 0.5rem;
  }

  .funding-detail-label, .funding-method-detail-label,
  .funding-detail-value, .funding-method-detail-value {
    color: rgba(255, 255, 255, 0.9);
  }
`;

interface FundingRequestReviewProps {
  submittedTransfer?: Transfer | null;
  transferFormData?: TransferFormData;
  achRelationship: AchRelationship;
};

const FundingRequestReview: FunctionComponent<FundingRequestReviewProps> = ({ submittedTransfer, transferFormData, achRelationship }) => (
  <StyledFundingRequestReview>
    <h5><Text id="fundYourAccountComponent.fundingDetails" /></h5>

    <div className="funding-details">
      <div className="funding-detail">
        <p className="funding-detail-label"><Text id="fundYourAccountComponent.depositAmount" /></p>
        <div className="funding-detail-value-wrapper">
          <p className="funding-detail-value">
            {formatNumber((submittedTransfer && submittedTransfer.amount) || (transferFormData && transferFormData.amount), { currency: true })}
          </p>
        </div>
      </div>
      {((transferFormData && transferFormData.transferIraContributionType) || (submittedTransfer && submittedTransfer.iraContribution)) && (
        <>
          <div className="funding-detail">
            <p className="funding-detail-label"><Text id="fundYourAccountComponent.iraContribution" /></p>
            <div className="funding-detail-value-wrapper">
              <p className="funding-detail-value">
                <Text
                  id={`formattedTransferIraContributionTypes.${(submittedTransfer && submittedTransfer.iraContribution && submittedTransfer.iraContribution.type) || (transferFormData && transferFormData.transferIraContributionType)}`}
                />
              </p>
            </div>
          </div>
          {
            (
              (submittedTransfer && submittedTransfer.iraContribution && submittedTransfer.iraContribution.applyToPriorYear !== undefined) ||
              (transferFormData && transferFormData.applyToPriorYear !== undefined)
            ) && (
              <div className="funding-detail">
                <p className="funding-detail-label">
                  <Text
                    id="fundYourAccountComponent.applyContributionToPriorYear"
                    data={{ year: new Date((submittedTransfer && submittedTransfer.createDate) || new Date()).getFullYear() - 1}}
                  />
                </p>
                <div className="funding-detail-value-wrapper">
                  <p className="funding-detail-value">
                    {
                      (submittedTransfer && submittedTransfer.iraContribution && submittedTransfer.iraContribution.applyToPriorYear) ||
                      (transferFormData && transferFormData.applyToPriorYear) ?
                      <Text id="yes" /> :
                      <Text id="no" />
                    }
                  </p>
                </div>
              </div>
            )
          }
          <div className="funding-detail">
            <p className="funding-detail-label"><Text id="fundYourAccountComponent.contributionYear" /></p>
            <div className="funding-detail-value-wrapper">
              <p className="funding-detail-value">
                {
                  (submittedTransfer && submittedTransfer.iraContribution && submittedTransfer.iraContribution.applyToPriorYear) ||
                  (transferFormData && transferFormData.applyToPriorYear) ?
                  new Date((submittedTransfer && submittedTransfer.createDate) || new Date()).getFullYear() - 1 :
                  new Date((submittedTransfer && submittedTransfer.createDate) || new Date()).getFullYear()
                }
              </p>
            </div>
          </div>
        </>
      )}
    </div>

    <h5><Text id="fundYourAccountComponent.fundingMethodDetails" /></h5>

    <div className="funding-method-details">
      <div className="funding-method-detail">
        <p className="funding-method-detail-label"><Text id="fundYourAccountComponent.bankName" /></p>
        <div className="funding-method-detail-value-wrapper">
          <p className="funding-method-detail-value">{achRelationship.bankName}</p>
        </div>
      </div>
      <div className="funding-method-detail">
        <p className="funding-method-detail-label"><Text id="fundYourAccountComponent.accountType" /></p>
        <div className="funding-method-detail-value-wrapper">
          <p className="funding-method-detail-value">{achRelationship.bankAccountType}</p>
        </div>
      </div>
      <div className="funding-method-detail">
        <p className="funding-method-detail-label"><Text id="fundYourAccountComponent.accountNickname" /></p>
        <div className="funding-method-detail-value-wrapper">
          <p className="funding-method-detail-value">{achRelationship.nickName}</p>
        </div>
      </div>
      <div className="funding-method-detail">
        <p className="funding-method-detail-label"><Text id="fundYourAccountComponent.accountNumber" /></p>
        <div className="funding-method-detail-value-wrapper">
          <p className="funding-method-detail-value">{achRelationship.bankAccount}</p>
        </div>
      </div>
      <div className="funding-method-detail">
        <p className="funding-method-detail-label"><Text id="fundYourAccountComponent.routingNumber" /></p>
        <div className="funding-method-detail-value-wrapper">
          <p className="funding-method-detail-value">{achRelationship.bankRoutingNumber}</p>
        </div>
      </div>
    </div>
  </StyledFundingRequestReview>
);

export default FundingRequestReview;
